import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Layout } from 'react-grid-layout';
import {
  createDashboard,
  deleteDashboard,
  getDashboard,
  listDashboards,
  updateDashboard,
  updateDashboardLayout,
} from '@/api/dashboards';
import { AddBlockDialog } from './AddBlockDialog';
import { DashboardGrid } from './DashboardGrid';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/auth/AuthContext';
import { LayoutDashboard, Pencil, Plus, Share2, Star, Trash2 } from 'lucide-react';

export function DashboardsView() {
  const { selectedEmployer } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteDashboardOpen, setDeleteDashboardOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(960);

  const employerKey = selectedEmployer?.id ?? 'none';

  const { data: dashboards = [], isLoading: listLoading } = useQuery({
    queryKey: ['dashboards', employerKey],
    queryFn: listDashboards,
  });

  useEffect(() => {
    if (!selectedId && dashboards.length > 0) {
      const preferred = dashboards.find((d) => d.isDefault) || dashboards[0];
      setSelectedId(preferred.id);
    }
  }, [dashboards, selectedId]);

  const { data: dashboard, isLoading: detailLoading } = useQuery({
    queryKey: ['dashboard', selectedId],
    queryFn: () => getDashboard(selectedId!),
    enabled: !!selectedId,
  });

  useEffect(() => {
    setTitleDraft(dashboard?.name ?? '');
  }, [dashboard?.id, dashboard?.name]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    setWidth(el.clientWidth || 960);
    return () => ro.disconnect();
  }, [selectedId]);

  const createMut = useMutation({
    mutationFn: () => createDashboard({ name: 'My dashboard' }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['dashboards', employerKey] });
      setSelectedId(created.id);
      setEditMode(true);
      toast({ title: 'Dashboard created' });
    },
  });

  const renameMut = useMutation({
    mutationFn: (name: string) => updateDashboard(selectedId!, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', selectedId] });
      queryClient.invalidateQueries({ queryKey: ['dashboards', employerKey] });
    },
    onError: (err: Error) => {
      setTitleDraft(dashboard?.name ?? '');
      toast({ variant: 'destructive', title: 'Rename failed', description: err.message });
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteDashboard(selectedId!),
    onSuccess: () => {
      setDeleteDashboardOpen(false);
      setEditMode(false);
      setSelectedId(null);
      queryClient.invalidateQueries({ queryKey: ['dashboards', employerKey] });
      toast({ title: 'Dashboard deleted' });
    },
  });

  const visibilityMut = useMutation({
    mutationFn: (visibility: 'private' | 'company') => updateDashboard(selectedId!, { visibility }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', selectedId] });
      queryClient.invalidateQueries({ queryKey: ['dashboards', employerKey] });
      toast({ title: 'Visibility updated' });
    },
  });

  const defaultMut = useMutation({
    mutationFn: () => updateDashboard(selectedId!, { isDefault: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards', employerKey] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', selectedId] });
    },
  });

  const layoutMut = useMutation({
    mutationFn: (layout: Array<{ i: string; x: number; y: number; w: number; h: number }>) =>
      updateDashboardLayout(selectedId!, {
        blocks: layout.map((l) => ({ id: l.i, x: l.x, y: l.y, w: l.w, h: l.h })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', selectedId] });
    },
  });

  const canEdit = !!dashboard?.isOwner;
  const blocks = useMemo(() => dashboard?.blocks ?? [], [dashboard?.blocks]);

  const commitTitle = () => {
    const next = titleDraft.trim();
    if (!dashboard || !canEdit) return;
    if (!next || next === dashboard.name) {
      setTitleDraft(dashboard.name);
      return;
    }
    renameMut.mutate(next);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5 shrink-0 text-primary" />
            {dashboard && editMode && canEdit ? (
              <Input
                className="h-9 max-w-md text-lg font-semibold"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
                disabled={renameMut.isPending}
                aria-label="Dashboard title"
              />
            ) : (
              <h1 className="truncate text-lg font-semibold">{dashboard?.name ?? 'Dashboards'}</h1>
            )}
            {dashboard && editMode && canEdit && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="shrink-0 text-destructive"
                onClick={() => setDeleteDashboardOpen(true)}
                title="Delete dashboard"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => createMut.mutate()}
            disabled={createMut.isPending}
          >
            <Plus className="mr-1 h-4 w-4" />
            Create
          </Button>

          {dashboards.length > 0 && (
            <Select
              value={selectedId ?? undefined}
              onValueChange={(v) => {
                setSelectedId(v);
                setEditMode(false);
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select dashboard" />
              </SelectTrigger>
              <SelectContent>
                {dashboards.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {dashboard && canEdit && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-9 w-9"
              onClick={() => {
                if (!dashboard.isDefault) defaultMut.mutate();
              }}
              disabled={dashboard.isDefault || defaultMut.isPending}
              title={dashboard.isDefault ? 'Default dashboard' : 'Set as default'}
              aria-label={dashboard.isDefault ? 'Default dashboard' : 'Set as default'}
              aria-pressed={dashboard.isDefault}
            >
              <Star
                className={
                  dashboard.isDefault
                    ? 'h-4 w-4 fill-amber-400 text-amber-400'
                    : 'h-4 w-4 text-muted-foreground'
                }
              />
            </Button>
          )}

          {dashboard && canEdit && (
            <>
              <Button
                type="button"
                size="sm"
                variant={editMode ? 'default' : 'outline'}
                onClick={() => setEditMode((v) => !v)}
              >
                <Pencil className="mr-1 h-4 w-4" />
                {editMode ? 'Done' : 'Edit layout'}
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setAddOpen(true)}>
                <Plus className="mr-1 h-4 w-4" />
                Add block
              </Button>
              <Button
                type="button"
                size="icon"
                variant={dashboard.visibility === 'company' ? 'default' : 'outline'}
                className="h-9 w-9"
                onClick={() =>
                  visibilityMut.mutate(dashboard.visibility === 'company' ? 'private' : 'company')
                }
                disabled={visibilityMut.isPending}
                title={
                  dashboard.visibility === 'company'
                    ? 'Shared with company'
                    : 'Private — click to share'
                }
                aria-label={
                  dashboard.visibility === 'company' ? 'Make private' : 'Share with company'
                }
                aria-pressed={dashboard.visibility === 'company'}
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-4" ref={containerRef}>
        {listLoading && <Skeleton className="h-40 w-full" />}

        {!listLoading && dashboards.length === 0 && (
          <div className="flex h-64 flex-col items-center justify-center gap-3 text-center">
            <p className="text-muted-foreground">Create your first dashboard to get started.</p>
            <Button type="button" onClick={() => createMut.mutate()}>
              Create dashboard
            </Button>
          </div>
        )}

        {selectedId && detailLoading && <Skeleton className="h-64 w-full" />}

        {dashboard && (
          <>
            <DashboardGrid
              dashboardId={dashboard.id}
              blocks={blocks}
              editMode={editMode}
              canEdit={canEdit}
              width={width}
              onLayoutChange={(layout: Layout) => layoutMut.mutate([...layout])}
            />

            <AddBlockDialog open={addOpen} onOpenChange={setAddOpen} dashboardId={dashboard.id} />

            <Dialog open={deleteDashboardOpen} onOpenChange={setDeleteDashboardOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Delete dashboard?</DialogTitle>
                  <DialogDescription>
                    This permanently deletes &quot;{dashboard.name}&quot; and all of its blocks.
                    This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDeleteDashboardOpen(false)}
                    disabled={deleteMut.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => deleteMut.mutate()}
                    disabled={deleteMut.isPending}
                  >
                    {deleteMut.isPending ? 'Deleting…' : 'Delete dashboard'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
}
